import type { StatusChipColor } from "components/StatusChip";

import {
  EPA_UPDATE_OVERDUE_DAYS,
  EXCEEDANCE_STAGE_LABELS,
} from "encamp-shared/src/constants/generatorDiscrepancyDefaults";
import {
  notificationDeadlineStatus,
  requiresPromptNotification,
} from "encamp-shared/src/constants/generatorStatusNotificationDeadlines";
import { DateTime } from "luxon";

type DeadlineFields = {
  state?: string | null;
  deadlineTier?: number | null;
  deadlineWindowDays?: number | null;
  deadlineDaysRemaining?: number | null;
};

function days(count: number): string {
  return `${count} ${count === 1 ? "day" : "days"}`;
}

export function deadlineChip(
  row: DeadlineFields
): { label: string; color: StatusChipColor } | null {
  const { deadlineWindowDays: windowDays, deadlineDaysRemaining: remaining } = row;
  if (windowDays == null || remaining == null) return null;
  switch (notificationDeadlineStatus(windowDays, remaining)) {
    case "IMMEDIATE":
      return { label: "Immediate", color: "error" };
    case "OVERDUE":
      return { label: `Overdue ${days(-remaining)}`, color: "error" };
    case "DUE_SOON":
      return { label: `Due in ${days(remaining)}`, color: "warning" };
    case "ON_TRACK":
      return { label: `Due in ${days(remaining)}`, color: "default" };
  }
}

/** Which rule applies, for the chip's tooltip and the drawer. */
export function deadlineBasis(row: DeadlineFields): string | null {
  const { deadlineWindowDays: windowDays, deadlineTier: tier, state } = row;
  if (windowDays == null || tier == null) return null;
  const counted =
    "Counted from when Encamp first found it, since shipment data arrives weeks late.";
  if (tier === 4) {
    return `No ${state ?? "state"} deadline applies to this change; Encamp recommends notifying within ${days(windowDays)}. ${counted}`;
  }
  if (windowDays === 0) return `${state} requires notifying immediately. ${counted}`;
  if (requiresPromptNotification(state)) {
    return `${state} requires notifying promptly; Encamp uses ${days(windowDays)}. ${counted}`;
  }
  return `${state} requires notifying within ${days(windowDays)}. ${counted}`;
}

/** Caption beside a waiting stage's chip; a pending EPA update is overdue past the follow-up window. */
export function stageWaiting(
  stage: string,
  row: { awaitingCorrectionSince?: string | null; awaitingEpaUpdateSince?: string | null },
  now = DateTime.now()
): { label: string; overdue: boolean } | null {
  if (stage === EXCEEDANCE_STAGE_LABELS.AWAITING_CORRECTION) {
    const label = waitingLabel(row.awaitingCorrectionSince, now);
    return label ? { label, overdue: false } : null;
  }
  if (stage !== EXCEEDANCE_STAGE_LABELS.AWAITING_EPA_UPDATE || !row.awaitingEpaUpdateSince) {
    return null;
  }
  const since = DateTime.fromISO(row.awaitingEpaUpdateSince, { zone: "utc" });
  const overdue = now.diff(since, "days").days >= EPA_UPDATE_OVERDUE_DAYS;
  const label = waitingLabel(row.awaitingEpaUpdateSince, now) ?? "";
  return { label: overdue ? `${label} · overdue` : label, overdue };
}

export function waitingLabel(
  since: string | null | undefined,
  now = DateTime.now()
): string | null {
  if (!since) return null;
  const waited = Math.max(
    0,
    Math.floor(now.diff(DateTime.fromISO(since, { zone: "utc" }), "days").days)
  );
  return `Waiting ${days(waited)}`;
}
